import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Button,
	TextField,
	List,
	ListItem,
	ListItemText,
	CircularProgress,
} from "@mui/material";
import { supabase } from "@/lib/supabase/supabaseClient";
import { useCentralStore } from "../../store/Central";
import { useJobStore } from "../../store/Job";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "../../store/Central/selectors";
import { Loading } from "@/components/src/animations/loading";
import {
	ClientSearchResult,
	PatientSearchResult,
} from "@/dentlab/src/store/Search/types";
import { CustomMobileDialog } from "./custom-mobile-dialog.component";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  code: string;
}

export const CreateJobDialog = ({
	open,
	onClose,
}: {
	open: boolean;
	onClose: (success: boolean, jobId?: string, jobDocumentId?: string) => void;
}) => {
	const [activeStep, setActiveStep] = useState(0);
	const [title, setTitle] = useState("");
  const [patient, setPatient] = useState<Patient>({
		first_name: "",
		last_name: "",
		code: "",
		id: '',
	})
	const [clientSearchQuery, setClientSearchQuery] = useState("");
	const [patientSearchQuery, setPatientSearchQuery] = useState("");
	const [clients, setClients] = useState<ClientSearchResult[]>([]);
	const [patients, setPatients] = useState<PatientSearchResult[]>([]);
	const [selectedClientId, setSelectedClientId] = useState<string | null>(
		null
	);
	const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
		null
	);
	const [loading, setLoading] = useState(true);
	const [range, setRange] = useState(10);
	const [errorMessage, setErrorMessage] = useState("");

	const { profile, clientsLookup } = useCentralStore.getState();
	const { createJobWithDeliveryNote } = useJobStore();

	useEffect(() => {
		fetchPatients();
	}, [range, patientSearchQuery, activeStep]);

	useEffect(() => {
		if (clientsLookup) {
			const clients = Object.values(clientsLookup).map((client) => ({
				...client,
				rank: 0,
			}));
			setClients(clients);
			setLoading(false);
		}
	}, [clientsLookup]);

	const fetchPatients = async () => {
		setLoading(true);
		try {
			const { data, error } = await supabase
				.from("patients")
				.select("*")
				.eq("organization_id", profile?.organization_id as string)
				.range(0, range - 1)
				.order("modified_at", { ascending: false });

			if (error) {
				Logger.log("Error fetching patients", error);
				showNotification({
					message: "Fehler beim Laden der Patienten",
					type: "error",
				});
				return;
			}

			const patients = data.map((patient) => ({
				...patient,
				rank: 0,
			}));

			setPatients(patients as PatientSearchResult[]);
		} catch (error) {
			Logger.log("Error fetching patients", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			searchClients();
		}
	}, [open, clientSearchQuery]);

	const searchClients = async () => {
		setLoading(true);
		try {
			if (!clientSearchQuery.trim()) {
				if (clientsLookup) {
					const clients = Object.values(clientsLookup).map(
						(client) => ({
							...client,
							rank: 0,
						})
					);
					setClients(clients);
				}
			} else {
				const { data, error } = await supabase.rpc("search_clients", {
					query: clientSearchQuery,
				});

				if (error || !data) {
					Logger.log("Error searching clients", error);
					showNotification({
						message: "Fehler beim Suchen der Auftraggeber",
						type: "error",
					});
					return;
				}

				setClients(data);
			}
		} catch (error) {
			Logger.log("Error searching clients", error);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) {
			searchPatients();
		}
	}, [open, patientSearchQuery]);

	const searchPatients = async () => {
		setLoading(true);
		try {
			const { data, error } = await supabase.rpc("search_patients", {
				query: patientSearchQuery,
				p_organization_id: profile?.organization_id as string,
			});

			if (error) {
				Logger.log("Error searching patients", error);
				showNotification({
					message: "Fehler beim Suchen der Patienten",
					type: "error",
				});
				return;
			}

			if (!data) {
				return;
			}

			setPatients(data);
		} catch (error) {
			Logger.log("Error searching patients", error);
		} finally {
			setLoading(false);
		}
	};

	const handleNext = (step: number) => {
		setActiveStep((prevStep) => prevStep + step);
	};

	const handleClose = () => {
		onClose(false);
		resetDialog();
	};

	const resetDialog = () => {
		setActiveStep(0);
		setTitle("");
		setClientSearchQuery("");
		setPatientSearchQuery("");
		setSelectedClientId(null);
		setSelectedPatientId(null);
		setErrorMessage("");
		setPatient({
			first_name: "",
			last_name: "",
			code: "",
			id: '',
		});
	};

	const selectClient = (clientId: string) => {
		setSelectedClientId(clientId);
		handleNext(1);
	};

	const selectPatient = (patientId: string) => {
		setSelectedPatientId(patientId);
		handleNext(2);
	};

	const createJob = async () => {
		setLoading(true);
		try {
			if (!selectedClientId) {
				showNotification({
					message: "Kein Auftraggeber ausgewählt",
					type: "error",
				});
				return;
			}

			const selectedClient = clients.find(
				(client) => client.id === selectedClientId
			);
			if (!selectedClient) {
				showNotification({
					message: "Ausgewählter Auftraggeber nicht gefunden",
					type: "error",
				});
				return;
			}

			const result = await createJobWithDeliveryNote(
				selectedClient.id,
				selectedPatientId || undefined,
				title || "Neuer Auftrag"
			);

			if (result.job && result.deliveryNote) {
				showNotification({
					message: "Auftrag erfolgreich erstellt",
					type: "success",
				});
				onClose(
					true,
					String(result.job.id),
					String(result.deliveryNote.id)
				);
			} else {
				showNotification({
					message: "Fehler beim Erstellen des Auftrags",
					type: "error",
				});
				onClose(false);
			}
		} catch (error) {
			Logger.log("Error creating job", error);
			showNotification({
				message: "Fehler beim Erstellen des Auftrags",
				type: "error",
			});
			onClose(false);
		} finally {
			setLoading(false);
			resetDialog();
		}
	};

  const createPatient = async () => {
    setLoading(true);
		setErrorMessage("");
    try {
      if (!patient?.first_name || !patient?.last_name) {
        return;
      }
      const organizationId = useCentralStore.getState().organization?.id;
      if (!organizationId) {
        return;
      }
      const { data, error } = await supabase
        .from(SupabaseTableEnum.PATIENTS)
        .insert({
          first_name: patient?.first_name,
          last_name: patient?.last_name,
          organization_id: organizationId,
          code: patient.code == "" ? '' : patient.code,
        })
        .select()
        .single();
      if (error) {
				Logger.log(data)
				Logger.log("Error creating patient", error);
				setErrorMessage("Fehler beim Erstellen des Patienten, existiert bereits ein Patient mit diesem Code?");
				return;
      }
      const patientId = data?.id;
      if (!patientId) {
        return;
      }

      setSelectedPatientId(patientId);
      handleNext(1);
    } catch (error) {
      Logger.log("Error creating patient 3", error);
    } finally {
      setLoading(false);
    }
  };

	const getStepTitle = () => {
		switch (activeStep) {
			case 0:
				return "Auftraggeber auswählen";
			case 1:
				return "Patient auswählen";
			case 2:
				return "Neuen Patient anlegen";
			case 3:
				return "Auftragstitel eingeben";
			default:
				return "";
		}
	};

	const renderStepContent = () => {
		switch (activeStep) {
			case 0: // Client selection
				return (
					<div className="w-full h-full">
						<div className="flex items-center mb-3">
							<TextField
								fullWidth
								placeholder="Auftraggeber suchen..."
								value={clientSearchQuery}
								onChange={(e) =>
									setClientSearchQuery(e.target.value)
								}
								size="small"
							/>
						</div>

						<div className="overflow-y-auto">
							{loading ? (
								<div className="flex justify-center my-4">
									<Loading />
								</div>
							) : (
								<List disablePadding>
									{clients.length > 0 ? (
										clients.map((client) => (
											<ListItem
												key={client.id}
												component="button"
												onClick={() =>
													selectClient(client.id)
												}
												className={`rounded mb-2 ${
													selectedClientId ===
													client.id
														? "bg-blue-200"
														: "bg-blue-100"
												}`}
											>
												<ListItemText
													primary={`${
														client.first_name || ""
													} ${
														client.last_name || ""
													}`}
													secondary={client.code}
												/>
											</ListItem>
										))
									) : (
										<div className="text-center py-4">
											Keine Auftraggeber gefunden
										</div>
									)}
								</List>
							)}
						</div>
					</div>
				);

			case 1: // Patient selection
				return (
					<div className="w-full h-full flex flex-col">
						<div className="z-10 bg-white w-full sticky top-0">
							<ListItem 
								component="button"
								onClick={() => handleNext(2)}
								className="rounded mb-2 bg-gray-100 h-10 border border-gray-300"
							>
								<ListItemText
									primary="Ohne Patient fortfahren"
								/>
							</ListItem>
							<ListItem 
								component="button"
								onClick={() => handleNext(1)}
								className="rounded mb-2 bg-green-100 h-10 border border-green-300"
								
							>
								<ListItemText
									primary="Neuen Patient anlegen"
								/>
							</ListItem>
							<div className="flex items-center mb-3 mt-3">
								<TextField
									fullWidth
									placeholder="Patient suchen..."
									value={patientSearchQuery}
									onChange={(e) =>
										setPatientSearchQuery(e.target.value)
									}
									size="small"
								/>
							</div>
						</div>
						<div className="flex-1 overflow-y-auto">
							{loading ? (
								<div className="flex justify-center my-4">
									<Loading />
								</div>
							) : (
								<List disablePadding>
									{patients.length > 0 ? (
										<>
											{patients.map((patient) => (
												<ListItem
													key={patient.id}
													component="button"
													onClick={() =>
														selectPatient(patient.id)
													}
													className={`rounded mb-2 ${
														selectedPatientId ===
														patient.id
															? "bg-blue-200"
															: "bg-blue-100"
													}`}
												>
													<ListItemText
														primary={`${patient.title || ""} ${
															patient.first_name || ""
														} ${patient.last_name || ""}`}
														secondary={patient.code}
													/>
												</ListItem>
											))}
											{patients.length == range && (
												<ListItem
													component="button"
													onClick={() => setRange(range + 10)}
													className="rounded mb-2 bg-gray-100"
												>
													<ListItemText primary="Weitere Patienten laden" />
												</ListItem>
											)}
										</>
									) : (
										<div className="text-center py-4">
											Keine Patienten gefunden
										</div>
									)}
								</List>
							)}
						</div>
					</div>
				);

      case 2:
        return(
					<form className="w-full h-full flex flex-col gap-2" onSubmit={(e) => {
            e.preventDefault();
            createPatient();
          }}>
            <TextField
              fullWidth
              label="Vorname"
              value={patient?.first_name}
              onChange={(e) => setPatient({...patient, first_name: e.target.value})}
              placeholder="Vorname"
              className="mb-4"
              required
            />
            <TextField
              fullWidth
              label="Nachname"
              value={patient?.last_name}
              onChange={(e) => setPatient({...patient, last_name: e.target.value})}
              placeholder="Nachname"
              className="mb-4"
              required
            />
            <TextField
              fullWidth
              label="Code"
              value={patient?.code}
              onChange={(e) => setPatient({...patient, code: String(e.target.value)})}
              placeholder="Code"
              className="mb-4"
            />
            <div className="text-sm text-gray-600 mb-2 mt-2">
              Wenn kein Code eingegeben wird, wird automatisch ein Code generiert.
            </div>
						{errorMessage && (
							<div className="text-sm text-red-500">
								{errorMessage}
							</div>
						)}
            <Button
              type="submit"
              variant="contained"
              sx={{
								backgroundColor: "#003261",
								color: "white",
								"&:hover": {
									backgroundColor: "#003261",
								},
							}}
              disabled={loading}
            >
               {loading ? <CircularProgress size={20} /> : 'Patient anlegen'}
            </Button>
          </form>
        )

			case 3: // Job title
				return (
					<div className="w-full flex flex-col gap-2">
						<TextField
							fullWidth
							label="Auftragstitel"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							placeholder="Neuer Auftrag"
							className="mb-4"
						/>

						<div className="text-sm text-gray-600 mb-4 mt-2">
							Wenn kein Titel eingegeben wird, wird automatisch
							"Neuer Auftrag" verwendet.
						</div>
						<Button
							onClick={createJob}
							variant="contained"
							sx={{
								backgroundColor: "#003261",
								color: "white",
								"&:hover": {
									backgroundColor: "#003261",
								},
							}}
						>
							 {loading ? <CircularProgress size={20} /> : 'Auftrag erstellen'}
						</Button>
					</div>
				);

			default:
				return null;
		}
	};

	return (
		<CustomMobileDialog
			open={open}
			onClose={handleClose}
			title={getStepTitle()}
		>
			<DialogContent className="p-0 h-full flex flex-col">
				<div className="w-full h-full flex-1">{renderStepContent()}</div>
			</DialogContent>
		</CustomMobileDialog>
	);
};
